import { useLocation, useSearchParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Add, Pencil } from '@factorialco/f0-react/icons/app'
import { BudgetFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import {
  dateBackendDateTimeToBackend,
  dateRangeWithYear,
  formatBackendDateToDayMonthYear
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { budgetStatusText } from 'modules/finance/lib/budgetTagInfo'
import {
  amountWithCurrency,
  limitLabelForSummary,
  periodicityOptions
} from 'modules/finance/lib/budgets'

interface BudgetDetailHeaderProps {
  budget?: BudgetFragment
  trainingGroupsCount?: number
}

const BudgetDetailHeader = ({ budget, trainingGroupsCount }: BudgetDetailHeaderProps) => {
  const [_searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const {
    access: { locale }
  } = useCurrentSession()
  const { has_dev_training_budgets } = useFeatures(['dev_training_budgets'])

  if (!budget) return null

  // Check if we're on the new tab-based UI (records or employees routes)
  const isOnNewTabUI =
    location.pathname.includes('/records') || location.pathname.includes('/employees')

  const periodLimit = periodicityOptions().find((option) => option.value == budget.periodicity)
  const periodLimitLabel = periodLimit?.label

  const budgetLimit = amountWithCurrency(Number(budget?.amountCents) || 0, budget.currency, locale)

  const budgetDate = () => {
    const effectiveFrom = dateBackendDateTimeToBackend(budget.effectiveFrom)
    if (!budget.effectiveUntil) {
      return formatBackendDateToDayMonthYear(effectiveFrom)
    }

    const effectiveUntil = dateBackendDateTimeToBackend(budget.effectiveUntil)
    return dateRangeWithYear(effectiveFrom, effectiveUntil)
  }

  const isExpensable = budget.budgetableBy === 'expensable'
  const isTrainingBudget = budget.budgetableBy === 'training_class'
  const budgetTypeLabel = {
    expensable: i18n.t('budgets.detail.budget_type.expensable'),
    training_class: i18n.t('budgets.detail.budget_type.training_class')
  }[budget.budgetableBy]

  const metadata = [
    {
      label: i18n.t('budgets.detail.budget_type.title'),
      value: {
        type: 'dot-tag',
        label: budgetTypeLabel,
        color: 'malibu'
      }
    } as const,

    isExpensable &&
      ({
        label: i18n.t('budgets.detail.time_frame'),
        value: {
          type: 'text',
          content: periodLimitLabel || ''
        }
      } as const),
    isExpensable &&
      ({
        label: limitLabelForSummary(budget.periodicity),
        value: {
          type: 'text',
          content: budgetLimit || '0'
        }
      } as const),
    {
      label: i18n.t('budgets.detail.date'),
      value: {
        type: 'date',
        formattedDate: budgetDate()
      }
    } as const,
    {
      label: i18n.t('budgets.detail.status'),
      value: {
        type: 'text',
        content: budget.archived
          ? i18n.t('budgets.detail.archived')
          : i18n.t('budgets.detail.active')
      }
    } as const,
    isTrainingBudget &&
      trainingGroupsCount !== undefined &&
      ({
        label: i18n.t('budgets.detail.training_groups'),
        value: {
          type: 'text',
          content: String(trainingGroupsCount)
        }
      } as const)
  ]

  const status = budget.currentStatus
  const statusText = budgetStatusText(status)
  const STATUS_MAP = {
    over_budget: { text: statusText, label: statusText, variant: 'critical' },
    budget_risk: { text: statusText, label: statusText, variant: 'warning' },
    within_budget: { text: statusText, label: statusText, variant: 'positive' }
  } as const

  // Hide the "Add Employee" button in header when using the new tab-based UI
  // (the button is now inside the EmployeesDataCollection component)
  const showAddEmployeeInHeader = isExpensable && !(has_dev_training_budgets && isOnNewTabUI)

  return (
    <ResourceHeader
      title={budget.name}
      description={budget.description ?? undefined}
      status={STATUS_MAP[budget.currentStatus] ?? STATUS_MAP.within_budget}
      metadata={metadata}
      primaryAction={
        showAddEmployeeInHeader
          ? {
              label: i18n.t('budgets.detail.add_employee'),
              icon: Add,
              onClick: () => setSearchParams({ modal: 'employee' })
            }
          : undefined
      }
      secondaryActions={[
        {
          label: i18n.t('budgets.detail.edit'),
          icon: Pencil,
          onClick: () => setSearchParams({ modal: 'edit' }),
          variant: 'outline'
        }
      ]}
    />
  )
}

export default BudgetDetailHeader
